import React from "react";
import { ArrowLeft, User, Pencil, FilePlus, Save, Download } from "lucide-react";

const WorkspaceNavbar = ({
  isScratchTheme,
  onBack,
  selectedChild,
  isStudentAccount,
  projectName,
  onProjectNameChange,
  onNewProject,
  onSaveProject,
  isSaving,
  isDirty,
  onDownloadProject,
  onOpenBackpack,
}) => {
  return (
    <div
      className={`px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0 z-30 transition-colors duration-200 ${
        isScratchTheme
          ? "bg-[#ffffff] border-b border-slate-200 text-slate-800 shadow-xs"
          : "bg-[#001428] border-b border-blue-900/60 text-white"
      }`}
    >
      {/* Left: Back Button & Child Display */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          title="Back to Dashboard"
          className={`w-9 h-9 rounded-full flex items-center justify-center transition duration-200 cursor-pointer flex-shrink-0 ${
            isScratchTheme
              ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300/70"
              : "bg-white/10 hover:bg-white/20 active:bg-white/30 text-white"
          }`}
        >
          <ArrowLeft
            className={`w-4 h-4 ${isScratchTheme ? "text-slate-700" : "text-white"}`}
          />
        </button>

        {selectedChild && !isStudentAccount && (
          <div
            className={`hidden sm:flex items-center gap-2 rounded-xl px-3.5 py-1.5 text-xs shadow-xs ${
              isScratchTheme
                ? "bg-purple-100/70 text-purple-800 border border-purple-300 font-extrabold"
                : "bg-[#001428] text-white border border-blue-900/40 font-black"
            }`}
          >
            <User
              className={`w-3.5 h-3.5 ${
                isScratchTheme ? "text-purple-700" : "text-purple-300"
              } flex-shrink-0`}
            />
            <span className="max-w-[100px] truncate">{selectedChild.name}</span>
          </div>
        )}
      </div>

      {/* Center: Project Naming */}
      <div className="flex-1 flex justify-center max-w-sm mx-auto">
        <div
          className={`relative flex items-center w-full max-w-[250px] rounded-lg transition-colors ${
            isScratchTheme
              ? "bg-slate-100 hover:bg-slate-200/80 focus-within:bg-white border border-slate-300"
              : "bg-white/5 hover:bg-white/10 focus-within:bg-[#001A33]"
          }`}
        >
          <input
            type="text"
            value={projectName}
            onChange={(e) => onProjectNameChange(e.target.value)}
            className={`w-full bg-transparent text-sm font-bold px-3 py-1.5 outline-none text-center ${
              isScratchTheme
                ? "text-slate-800 placeholder-slate-400"
                : "text-white placeholder-slate-400"
            }`}
            placeholder="Project Name"
          />
          <Pencil
            className={`w-3.5 h-3.5 absolute right-2 pointer-events-none ${
              isScratchTheme ? "text-slate-500" : "text-slate-400"
            }`}
          />
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Project Action Buttons */}
        <button
          type="button"
          onClick={onNewProject}
          title="New Project"
          className={`p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs flex items-center gap-1.5 transition cursor-pointer ${
            isScratchTheme
              ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
              : "bg-white/5 hover:bg-blue-500/30 text-blue-300"
          }`}
        >
          <FilePlus className={`w-4 h-4 ${isScratchTheme ? "text-slate-600" : ""}`} />
          <span className="hidden lg:inline">New</span>
        </button>

        <div
          className={`w-px h-5 mx-1 hidden sm:block ${
            isScratchTheme ? "bg-slate-300" : "bg-slate-700/50"
          }`}
        />

        <button
          type="button"
          onClick={onSaveProject}
          disabled={isSaving}
          title={isDirty ? "Save to Cloud (Unsaved Changes)" : "Save to Cloud"}
          className={`p-2 rounded-lg shadow-sm flex items-center justify-center transition cursor-pointer disabled:opacity-50 relative ${
            isScratchTheme
              ? "bg-purple-600 text-white hover:bg-purple-500 shadow-purple-500/20"
              : "bg-white/5 hover:bg-blue-600/80 text-white"
          }`}
        >
          {isDirty && !isSaving && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full animate-pulse" />
          )}
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4 text-white" />
          )}
        </button>

        <button
          type="button"
          onClick={onDownloadProject}
          title="Download to Computer"
          className={`p-2 rounded-lg shadow-xs flex items-center justify-center transition cursor-pointer ${
            isScratchTheme
              ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
              : "bg-white/5 hover:bg-emerald-600/80 text-white"
          }`}
        >
          <Download className={`w-4 h-4 ${isScratchTheme ? "text-slate-600" : ""}`} />
        </button>

        <button
          type="button"
          onClick={onOpenBackpack}
          title="My Backpack"
          className={`p-1.5 sm:px-3 sm:py-1.5 rounded-lg text-xs font-extrabold shadow-xs flex items-center gap-1.5 transition cursor-pointer ${
            isScratchTheme
              ? "bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-300"
              : "bg-white/5 hover:bg-purple-600/80 text-white"
          }`}
        >
          <svg
            className={`w-4 h-4 ${isScratchTheme ? "text-purple-600" : "text-purple-300"}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            <path d="M8 21v-5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v5" />
            <path d="M8 10h8" />
          </svg>
          <span className="hidden sm:inline">Backpack</span>
        </button>
      </div>
    </div>
  );
};

export default WorkspaceNavbar;
